import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';
import { ErroApi } from '../../nucleo/api';
import { criarDesafioMfa, verificarMfa, type DesafioMfa } from '../../nucleo/mfa/api';
import { MfaBloqueado, MfaCarregando, MfaFormularioCodigo, MfaMensagem, useRelogioMfa } from '../../nucleo/mfa/componentes';
import { bloqueioMfa, bloqueioVigente, erroMfa } from '../../nucleo/mfa/estado';
import type { AcaoMfa } from '../../nucleo/mfa/ponte';
import { Botao } from '../../nucleo/ui/Sobreposicoes';
import './Sala.css';

/** Novo painel da Sala; a ponte no cliente do núcleo o abre sem alterar as telas da frente Diff. */
export function MfaPainel({ acao, aoContinuar, aoInscrever }: { acao: AcaoMfa; aoContinuar: () => void; aoInscrever?: () => void }) {
  const { t } = useTranslation('mfa');
  const [estado, setEstado] = useState<'carregando' | 'pronto' | 'erro' | 'sucesso'>('carregando');
  const [desafio, setDesafio] = useState<DesafioMfa | null>(null);
  const [codigo, setCodigo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [bloqueadoAte, setBloqueadoAte] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const vivo = useRef(true);
  const agora = useRelogioMfa();
  const bloqueado = bloqueioVigente(bloqueadoAte, agora);
  const expirado = !!desafio && Date.parse(desafio.expiraEm) <= agora;
  useEffect(() => { vivo.current = true; return () => { vivo.current = false; }; }, []);

  async function carregar(signal?: AbortSignal) {
    setEstado('carregando'); setErro(null); setCodigo('');
    try {
      const novo = await criarDesafioMfa(signal);
      if (vivo.current && !signal?.aborted) { setDesafio(novo); setBloqueadoAte(null); setEstado('pronto'); }
    } catch (e) {
      if (vivo.current && !signal?.aborted) {
        setErro(erroMfa(e)); setBloqueadoAte(bloqueioMfa(e)); setEstado('erro');
      }
    }
  }
  useEffect(() => {
    if (acao !== 'verificar') return;
    const abortar = new AbortController(); void carregar(abortar.signal);
    return () => abortar.abort();
  }, [acao]);
  async function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    if (!desafio || ocupado || bloqueado || expirado || codigo.length !== 6) return;
    setOcupado(true); setErro(null);
    const valor = codigo; setCodigo('');
    try {
      await verificarMfa(valor, desafio.desafioId);
      if (vivo.current) { setDesafio(null); setEstado('sucesso'); }
    } catch (e) {
      if (vivo.current) {
        setErro(erroMfa(e)); setBloqueadoAte(bloqueioMfa(e));
        if (e instanceof ErroApi && e.code === 'mfa_desafio_invalido') setDesafio(null);
      }
    } finally { if (vivo.current) setOcupado(false); }
  }
  if (acao === 'inscrever') return <div data-sala-mfa className="space-y-5">
    <span className="inline-flex size-12 items-center justify-center rounded-xl bg-acento-suave text-acento-texto"><ShieldCheck size={23} /></span>
    <p className="text-sm leading-relaxed text-tinta-2">{t('convite')}</p>
    <Link to="/conta" onClick={aoInscrever} className="inline-flex min-h-11 w-full items-center justify-center rounded-lg bg-acento px-4 text-sm font-medium text-acento-tinta hover:bg-acento-forte focus-visible:outline focus-visible:outline-2 focus-visible:outline-acento sm:w-auto">{t('inscrever')}</Link>
  </div>;
  if (estado === 'sucesso') return <div data-sala-mfa className="space-y-5"><MfaMensagem tipo="sucesso"><strong className="block">{t('confirmado')}</strong><p className="mt-1">{t('confirmadoTexto')}</p></MfaMensagem><Botao onClick={aoContinuar} className="min-h-11 w-full sm:w-auto">{t('continuar')}</Botao></div>;
  return <div data-sala-mfa className="space-y-5">
    <p className="text-sm leading-relaxed text-tinta-2">{t('verificarTexto')}</p>
    {estado === 'carregando' ? <MfaCarregando /> : <>
      {bloqueado ? <MfaBloqueado ate={bloqueadoAte} /> : erro && <MfaMensagem tipo="erro">{t(erro)}</MfaMensagem>}
      {expirado && !bloqueado && <MfaMensagem tipo="aviso">{t('desafioExpirado')}</MfaMensagem>}
      {desafio && <MfaFormularioCodigo codigo={codigo} aoMudar={setCodigo} aoEnviar={enviar} ocupado={ocupado} desabilitado={bloqueado || expirado} />}
      {(!desafio || expirado || estado === 'erro') && <Botao variante="secundario" disabled={bloqueado} onClick={() => void carregar()} className="min-h-11 w-full sm:w-auto">{t('novoDesafio')}</Botao>}
    </>}
  </div>;
}
